<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import useTable from '@/composables/useTable';
import * as commonConstants from '@/constants/common';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: string;
  customer: string;
  title: string;
  isRfc: boolean;
  status: string;
  statusIcon?: string;
  updated: string;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'customer',
      title: 'Customer',
      isNotSortable: true,
    },
    {
      value: 'title',
      title: 'Title',
      isNotSortable: true,
    },
    {
      value: 'rfc',
      title: 'RFC',
      isNotSortable: true,
    },
    {
      value: 'status',
      title: 'Status',
      isNotSortable: true,
    },
    {
      value: 'updated',
      title: 'Updated',
      isNotSortable: true,
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length;
});

const timeZone = 'Australia/Sydney';

// TODO common formatDate function for all components
// maybe it needs two params (updated, updatedTitle)
function formatDate(date: string, timeZone: string): string {
  try {
    return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
  } catch (err) {
    return commonConstants.EM_DASH;
  }
}
</script>

<template>
  <table
    :class="[tableStyles.table, $style.detailedJobTicketsTable]"
    data-testid="detailedJobTicketsTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText>No tickets</TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr>
          <td :class="$style.customerTd">
            {{ dataItem.customer }}
          </td>
          <td :class="$style.titleTd">
            {{ dataItem.title }}
          </td>
          <td :class="$style.rfcTd">
            <v-icon
              v-if="dataItem.isRfc"
              :class="$style.rfcIcon"
              icon="mdi-frequently-asked-questions"
            >
            </v-icon>
            <template v-else>
              {{ commonConstants.EM_DASH }}
            </template>
          </td>
          <td :class="$style.statusTd">
            <v-icon
              v-if="dataItem.statusIcon"
              :class="$style.icon"
              :icon="dataItem.statusIcon"
            />
            {{ dataItem.status }}
          </td>
          <td :class="$style.updatedTd">
            <span>
              <KParentTooltip>{{ dataItem.updated }}</KParentTooltip>
              {{ formatDate(dataItem.updated, timeZone) }}
            </span>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobTicketsTable {
}
  .customerTh {
  }
  .titleTh {
  }
  .rfcTh {
  }
  .statusTh {
  }
  .updatedTh {
  }
  .customerTd {
  }
  .titleTd {
  }
  .rfcTd {
  }
    .rfcIcon {
      color: colors.$color-orange-400;
      font-size: 20px;
    }
  .statusTd {
    white-space: nowrap;
  }
    .statusIcon {
      font-size: 20px;
    }
  .updatedTd {
  }
</style>
